import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  reduce, map, reject as lodashReject,
  filter, keyBy,
} from 'lodash';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import {
  Button, Modal,
  FormGroup, ControlLabel, FormControl, HelpBlock,
} from 'react-bootstrap';
import Joi from 'joi';
import Fa from 'react-fontawesome';
import { fetchManagers, cloneTeam } from '../../TeamActions';
import UserSelector from '../../../../components/multi-user-selector';

class ShareModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      values: [],
      success: false,
    };
    this.close = this.close.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.renderField = this.renderField.bind(this);
    this.onSelectUsers = this.onSelectUsers.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.show !== nextProps.show && nextProps.show && nextProps.team) {
      this.props.initialize(nextProps.team);
    }
  }

  close() {
    if (!this.props.submitting) {
      this.setState({ values: [], success: false });
      this.props.onClose();
    }
  }

  onSelectUsers(values) {
    this.setState({ values });
  }

  handleSubmit(data) {
    const { values } = this.state;
    const { submitting } = this.props;
    if (!submitting) {
      const { team } = this.props;
      const p = this.props.dispatch(cloneTeam(team._id, map(values, '_id'), data.name));
      p.then(
        ({ teams }) => {
          const indexedTeams = keyBy(teams, 'user__id');
          const newValues = reduce(values, (items, value) => {
            items.push({
              ...value,
              success: indexedTeams[value._id] !== undefined,
            });
            return items;
          }, []);
          this.setState({ success: true, values: newValues });
        },
        () => {
        },
      );
      return p;
    }
  }

  renderField({
    input, label, type, componentClass, meta: { touched, error },
  }) {
    const { submitting } = this.props;
    const validationState = touched && error ? 'error' : null;
    return (
      <FormGroup validationState={validationState} className="">
        <ControlLabel>{label}</ControlLabel>
        <FormControl {...input} componentClass={componentClass} placeholder={label} type={type} disabled={submitting} />
        <FormControl.Feedback />
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderContent() {
    const { submitting } = this.props;
    return (
      <div>
        <Field
          name="name"
          type="text"
          component={this.renderField}
          label="Team Name"
        />
        <FormGroup>
          <UserSelector 
            label="Select user(s) to share"
            fetchUsers={fetchManagers}
            onSelect={this.onSelectUsers}
            disabled={submitting}
          />
        </FormGroup>
      </div>
    );
  }

  renderSuccess() {
    const { values } = this.state;
    const valid = filter(values, { success: true });

    if (valid.length === 0) {
      return (
        <div className="alert alert-danger">
          This team has not been shared with any users.
        </div>
      );
    }

    return (
      <div className="alert alert-success">
        The team <strong>{this.props.teamName}</strong> has been shared to:
        <ul>
          {valid.map(u => (<li key={u._id}>{u.display_name}</li>))}
        </ul>
      </div>
    );
  }

  render() {
    const {
      show, team, handleSubmit, submitting,
    } = this.props;

    const { values, success } = this.state;

    const icon = submitting ? 'spinner' : 'share';
    const btn = submitting ? 'Sharing' : 'Share';

    const valid = values.length > 0;

    const title = team ? `Share team: ${team.name}` : 'Share Team';

    return (
      <div>
        <Modal
          show={show}
          onHide={this.close}
          keyboard={false}
        >
          <Modal.Header closeButton={!submitting}>
            <Modal.Title>
              {title}
            </Modal.Title>
          </Modal.Header>
          <form onSubmit={handleSubmit(this.handleSubmit)}>
            <Modal.Body>
              {!success &&
                <div>
                  {team !== null && this.renderContent()}
                </div>
              }
              {success && this.renderSuccess()}
            </Modal.Body>
            <Modal.Footer>
              {!success &&
                <div className="clearfix">
                  <Button
                    bsStyle="primary"
                    className="pull-right"
                    type="submit"
                    disabled={submitting || !valid || this.props.invalid}
                  >
                    <Fa name={icon} spin={submitting} /> {btn}
                  </Button>
                  <Button
                    bsStyle="link"
                    className="pull-left"
                    onClick={this.close}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                </div>
              }
              {success &&
                <Button
                  bsStyle="link"
                  onClick={this.close}
                >
                  Done
                </Button>
              }
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    );
  }
}

// Retrieve data from store as props
const selector = formValueSelector('teamShareForm'); // <-- same as form name
function mapStateToProps(state) {
  return {
    teamName: selector(state, 'name'),
  };
}

ShareModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  team: PropTypes.shape({
    name: PropTypes.string,
  }),
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  initialize: PropTypes.func,
  invalid: PropTypes.bool,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  teamName: PropTypes.string,
};

ShareModal.defaultProps = {
  team: null,
  show: false,
  onClose: () => {},
  onSuccess: () => {},
  initialize: () => {},
  invalid: true,
  submitting: false,
  handleSubmit: () => {},
  teamName: null,
};


const validate = (data) => {
  // Legacy constraints for reference:
  // const constraints = {
  //   name: {
  //     presence: true,
  //     length: {
  //       minimum: 1,
  //       maximum: 1024,
  //     },
  //   },
  // };

  const schema = Joi.object({
    name: Joi.string().min(1).max(1024).required().messages({
      'string.base': 'Name must be a string',
      'string.empty': 'Name is required',
      'any.required': 'Name is required',
      'string.min': 'Name must be at least 1 character',
      'string.max': 'Name must be at most 1024 characters',
    }),
  }).unknown(true);

  const { error } = schema.validate(data, { abortEarly: false });
  if (!error) return {};
  
  return error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {});
};


const TeamShareForm = reduxForm({
  form: 'teamShareForm',
  validate,
})(ShareModal);

export default connect(mapStateToProps)(TeamShareForm);
